/**
 * Forêt sèche (GDD section 10): sand, a dense forest of wooden trees, black powder buried in the rock, and
 * rare water holes. Dangers: forest fires pushed by the wind. Key: read the wind before setting fire; the
 * Wave becomes vital.
 */
import { Physique, nextInt, sinDeg, TRIG_ONE } from '@invaders/sim';
import { plainMap, type MapState } from '../state';
import { type Gen, ellipse, island, layer, rootedTree, sea, settle, waves } from './common';

const { EMPTY, ROCK, SAND, WATER, BLACK_POWDER } = Physique;

export function generateForest(g: Gen): MapState {
  const { w, rng, data } = g;
  const coastL = Math.trunc((w * 11) / 100) + nextInt(rng, 13) - 6;
  const coastR = Math.trunc((w * 89) / 100) + nextInt(rng, 13) - 6;
  const relief = waves(g, 26, 3, 12, 7, 4, 19);

  // Two water holes: bowls dug into the relief.
  const ponds: [number, number][] = [];
  const third = Math.trunc((coastR - coastL) / 3);
  for (let k = 0; k < 2; k++) ponds.push([coastL + third * (k + 1) + nextInt(rng, 81) - 40, 26 + nextInt(rng, 10)]);
  const base = (x: number) => 74 + relief(x);
  const elevation = (x: number) => {
    let e = base(x);
    for (const [cx, half] of ponds) {
      const d = Math.abs(x - cx);
      const rim = Math.min(base(cx - half), base(cx + half));
      // From the relief at the edge down to 28 cells under the lower rim in the middle.
      if (d < half) e -= Math.trunc(((e - rim + 28) * sinDeg(Math.trunc((90 * (half - d)) / half))) / TRIG_ONE);
    }
    return e;
  };
  const surface = island(g, coastL, coastR, 80, elevation);
  const rock = surface.slice();

  // Black powder buried in the rock, here and there.
  for (let k = 0, n = 6 + nextInt(rng, 3); k < n; k++) {
    const cx = coastL + 60 + nextInt(rng, coastR - coastL - 120);
    ellipse(g, cx, rock[cx] + 14 + nextInt(rng, 20), 9 + nextInt(rng, 9), 4 + nextInt(rng, 3), BLACK_POWDER, ROCK);
  }

  // The water holes, a few cells under their rim.
  for (const [cx, half] of ponds) {
    const top = Math.max(surface[cx - half], surface[cx + half]) + 3;
    for (let x = cx - half; x <= cx + half; x++) for (let y = top; y < surface[x]; y++) if (data[y * w + x] === EMPTY) data[y * w + x] = WATER;
  }

  // Sand over everything but the water holes.
  const p = nextInt(rng, 360);
  const pondDist = (x: number) => Math.min(...ponds.map(([cx, half]) => Math.abs(x - cx) - half - 4));
  const nearPond = (x: number) => pondDist(x) <= 0;
  layer(g, surface, coastL - 60, coastR + 60, (x) => {
    const c = Math.min(x - coastL, coastR - x);
    const dune = sinDeg(Math.trunc((x * 360 * 11) / w) + p);
    const depth = c < -40 ? 2 : 6 + Math.trunc((8 * (dune > 0 ? dune : 0)) / TRIG_ONE);
    const d = pondDist(x);
    return d <= 0 ? 0 : d < 12 ? Math.trunc((depth * d) / 12) : depth; // thinner towards the water holes
  }, SAND);

  // A dense forest: a tree every 14 to 26 cells, rooted in the rock under the sand.
  for (let x = coastL + 36; x < coastR - 36; x += 14 + nextInt(rng, 13)) {
    if (nearPond(x) || Math.abs(rock[x - 6] - rock[x + 6]) > 9) continue;
    rootedTree(g, x, rock[x], surface[x], 26 + nextInt(rng, 24));
  }

  sea(g);
  settle(g);
  return plainMap('forest');
}
